import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { NativeSelect } from '@mantine/core';
import { EMPTY_PARTS, datePartsToYmd, daysInMonth, toDateParts, updateDateParts, type DateParts } from '@/lib/dateParts';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export interface ExpiryDateInputProps {
  /** 'YYYY-MM-DD' (or a local Date). null while empty. */
  value: string | Date | null | undefined;
  /** Called with 'YYYY-MM-DD' once year, month and day are all chosen; null otherwise. */
  onChange: (value: string | null) => void;
  /** Defaults to "Expiry date". */
  label?: ReactNode;
  description?: ReactNode;
  /** Error from the parent (wins over the built-in "date has passed" message). */
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Earliest acceptable 'YYYY-MM-DD' (e.g. today or tomorrow); an earlier full date shows a message. */
  minDate?: string | null;
  /** How many years ahead to offer (default 15: passports run 10 years, licence cards 5). */
  yearsAhead?: number;
}

/**
 * Expiry date as three dropdowns in the order SA licence cards print it (year, month, day), instead
 * of a calendar pop-up. Phones show their own large wheel pickers for native selects, and months show
 * their number too ("05 May") so people can copy the card's 2029/05/19 straight across.
 */
export function ExpiryDateInput({
  value, onChange, label = 'Expiry date', description, error, required, disabled, minDate, yearsAhead = 15,
}: ExpiryDateInputProps) {
  const id = useId();
  const [parts, setParts] = useState<DateParts>(() => toDateParts(value));

  // Follow the parent when it sets a complete date (a saved profile loading) or clears the field (after sending).
  const valueKey = value instanceof Date ? (Number.isNaN(value.getTime()) ? '' : `${value.getFullYear()}-${value.getMonth()}-${value.getDate()}`) : value ?? '';
  useEffect(() => {
    const next = toDateParts(value);
    if (next.y && next.m && next.d) setParts(next);
    else if (!value && datePartsToYmd(parts)) setParts(EMPTY_PARTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const thisYear = new Date().getFullYear();
  const years = useMemo(() => {
    const list: string[] = [];
    const chosen = Number(parts.y);
    const first = chosen && chosen < thisYear ? chosen : thisYear; // keep an older saved year visible
    for (let y = first; y <= thisYear + Math.max(1, yearsAhead); y++) list.push(String(y));
    return list;
  }, [thisYear, yearsAhead, parts.y]);

  const maxDay = daysInMonth(Number(parts.m), Number(parts.y));
  const days = useMemo(() => Array.from({ length: maxDay }, (_, i) => String(i + 1)), [maxDay]);

  const update = (patch: Partial<DateParts>) => {
    const next = updateDateParts(parts, patch);
    setParts(next);
    onChange(datePartsToYmd(next));
  };

  const ymd = datePartsToYmd(parts);
  const tooEarly = !!ymd && !!minDate && ymd < minDate;
  const shownError = error ?? (tooEarly ? 'That date has already passed. Check the expiry date on the document.' : undefined);
  const invalid = !!shownError;

  return (
    <fieldset className="rides-fieldset" aria-describedby={[description ? `${id}-desc` : '', shownError ? `${id}-err` : ''].filter(Boolean).join(' ') || undefined}>
      <legend>
        {label}
        {required && <span className="rides-req" aria-hidden="true">*</span>}
      </legend>
      <div className="rides-ymd">
        <NativeSelect
          aria-label="Year"
          value={parts.y}
          onChange={(e) => update({ y: e.currentTarget.value })}
          data={[{ value: '', label: 'Year' }, ...years.map((y) => ({ value: y, label: y }))]}
          error={invalid ? true : undefined}
          required={required}
          disabled={disabled}
        />
        <NativeSelect
          aria-label="Month"
          value={parts.m}
          onChange={(e) => update({ m: e.currentTarget.value })}
          data={[{ value: '', label: 'Month' }, ...MONTHS.map((name, i) => ({ value: String(i + 1), label: `${String(i + 1).padStart(2, '0')} ${name}` }))]}
          error={invalid ? true : undefined}
          required={required}
          disabled={disabled}
        />
        <NativeSelect
          aria-label="Day"
          value={parts.d}
          onChange={(e) => update({ d: e.currentTarget.value })}
          data={[{ value: '', label: 'Day' }, ...days.map((d) => ({ value: d, label: d }))]}
          error={invalid ? true : undefined}
          required={required}
          disabled={disabled}
        />
      </div>
      {description && <p className="desc" id={`${id}-desc`}>{description}</p>}
      {shownError && <p className="err" id={`${id}-err`} role="alert">{shownError}</p>}
    </fieldset>
  );
}
